/**
 * @file CASSIVoucher Component
 * @description This component renders a Sales Invoice (SI) voucher for the CAS (Central Accounting System).
 * It displays detailed information about a specific sales invoice, including customer details,
 * transaction information, and room posting details.
 */

import FormPDF, { IFormInput, IFormPDFRef } from '@components/pdf/form-pdf';
import { TableRow } from '@components/pdf/react-pdf/react-pdf.interface';
import useAppContext from '@hooks/useAppContext';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useLazyGetCASSIDetailsQuery,
  useLazyGetCASTransactionQuery,
} from '../../api/cas/cas.api.rtk';
import useToast from '../../hooks/useToast';
import {
  CASGetSIDetails,
  CASGetSIDetailsRoomPosting,
  CASGetVSIDetails,
} from '../../interfaces/cas.interface';
import {
  dateFormat,
  formatNumber,
  pdfValuesToString,
  toReportInputs,
} from '../../utils/format';
import moment from 'moment';

/**
 * @typedef {Object} CASSIVoucherProps
 * @property {number || string} [si_no='1,2,3'] - The Sales Invoice number
 * @property {boolean} [isOpen=true] - Whether the voucher modal is open
 * @property {() => void} [onClose] - Function to call when closing the voucher
 */

/**
 * CASSIVoucher Component
 * @param {CASSIVoucherProps} props - The component props
 * @returns {JSX.Element} The rendered component
 */
const CASSIVoucher = ({
  si_no,
  isOpen,
  onClose,
  transType = 'SI',
}: {
  si_no?: string | number;
  isOpen?: boolean;
  onClose?: () => void;
  transType?: 'SI' | 'VSI';
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleCloseClick = () => {
    !onClose && navigate(-1);
    onClose?.();
  };

  const {
    genpar: { getGenparValue },
    startLoading,
    stopLoading,
  } = useAppContext();

  const casAcn = getGenparValue('cas_acn');
  const casAcnDate = getGenparValue('cas_acn_date');
  const siSeries = getGenparValue('si_series');

  const [getCASTransaction] = useLazyGetCASTransactionQuery();
  const [getCASRoomPostingData] = useLazyGetCASSIDetailsQuery();

  const formRef = useRef<IFormPDFRef>(null);

  const getSIDetails = async ({
    transType,
    si_no,
  }: {
    transType: 'SI' | 'VSI';
    si_no?: string | number;
  }) => {
    try {
      const casSiData = await getCASTransaction({
        params: { casType: transType },
        query: { casID: si_no }, //155
      }).unwrap();

      // Get SI Header Data
      const siHeaderData =
        transType === 'SI'
          ? (casSiData.data as CASGetSIDetails[])
          : (casSiData.data as CASGetVSIDetails[]);

      const siRoomPostingDetails: CASGetSIDetailsRoomPosting[] = [];

      for (const siData of siHeaderData) {
        if (siData.gross && siData.transactionIdentifier && 'src' in siData) {
          const result = await getCASRoomPostingData({
            transType: siData.src,
            transID: siData.transactionIdentifier,
          }).unwrap();

          siRoomPostingDetails.push(...(result.data?.getGuestLedgerData || []));
        }
      }

      return {
        siDetails: casSiData.data,
        siRoomPostingDetails: siRoomPostingDetails.filter(Boolean),
      };
    } catch (error) {
      toast({
        message: error,
        severity: 'error',
      });

      return {
        siDetails: null,
        siRoomPostingDetails: null,
      };
    }
  };

  const handlePrint = async () => {
    try {
      startLoading();
      if (!formRef.current) {
        toast({ severity: 'warning', message: 'No PDF Component Found' });
        return;
      }

      const { getHeaderDetails, getPropertyLogo, handleShowPDF, getTemplate } =
        formRef.current;

      const template = await getTemplate({
        department: 'FO',
        category: 'Reservations',
        templateName: 'Sales Invoice',
      });

      if (!template) return;

      const { siDetails, siRoomPostingDetails } = await getSIDetails({
        transType,
        si_no,
      });

      const { propertyLogo } = await getPropertyLogo();
      const headerDetails = getHeaderDetails({ moduleName: 'Xenia FOS' });
      const inputs: IFormInput[] = (siDetails ?? []).map((newItem) => {
        const siDetail = pdfValuesToString(newItem);

        const roomPostingDetails =
          siRoomPostingDetails?.filter(
            (detail) =>
              detail?.transaction_identifier ===
                siDetail?.transactionIdentifier &&
              detail?.dr_cr_adj === 'Credit',
          ) ?? [];

        const charges: TableRow[] = [
          ...roomPostingDetails.map(
            (item): TableRow => ({
              cells: [
                {
                  value: item.posting_date?.toString()
                    ? moment(item.posting_date).format(dateFormat)
                    : '',
                  id: 'posting_date',
                },
                { value: item.item?.toString() ?? '', id: 'item' },
                { value: '1', id: 'qty' }, // static from the previous logic
                {
                  value: `${formatNumber(item.gross_amount, 2, 2)}`,
                  id: 'gross_amount',
                },
                {
                  value: `${formatNumber(item.discount, 2, 2)}`,
                  id: 'discount',
                },
                { value: `${formatNumber(item.vat, 2, 2)}`, id: 'vat' },
                { value: `${formatNumber(item.net, 2, 2)}`, id: 'net' },
              ],
            }),
          ),
        ];

        const footerCharges: TableRow[] = [
          {
            cells: [
              {
                value: formatNumber(siDetail?.gross as string, 2, 2) ?? '-',
                id: 'gross',
              },
            ],
          },
        ];

        return {
          ...(propertyLogo && { propertyLogo }),
          ...headerDetails,
          ...toReportInputs(siDetail ?? {}),
          roomType: String(
            transType === 'SI'
              ? (siRoomPostingDetails?.find(
                  (d) =>
                    d?.transaction_identifier ===
                    siDetail?.transactionIdentifier,
                )?.room_code ?? '')
              : (siDetail?.roomCode ?? ''),
          ),
          roomNo: String(
            transType === 'SI'
              ? (siRoomPostingDetails?.find(
                  (d) =>
                    d?.transaction_identifier ===
                    siDetail?.transactionIdentifier,
                )?.room_number ?? '')
              : (siDetail?.roomNumber ?? ''),
          ),
          nights: String(
            transType === 'SI'
              ? (siRoomPostingDetails?.find(
                  (d) =>
                    d?.transaction_identifier ===
                    siDetail?.transactionIdentifier,
                )?.nights ?? '')
              : (siDetail?.nights ?? ''),
          ),
          charges,
          footerCharges,
          casAcn,
          casAcnDate,
          siSeries,
          postingDate: siDetail?.postingDate
            ? moment(siDetail.postingDate).format(dateFormat)
            : '',
        };
      });

      handleShowPDF({
        template,
        inputs,
      });
    } catch (error) {
      handleCloseClick?.();
      toast({ message: error, severity: 'warning' });
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    (async () => await handlePrint())();
  }, [isOpen]);

  return (
    <FormPDF
      {...{
        ref: formRef,
        title: transType === 'SI' ? 'Sales Invoice' : 'Voided Sales Invoice',
        modalOnClose: handleCloseClick,
      }}
    />
  );
};

export default CASSIVoucher;
